// src/main/shell/symbolGrid/indexerPagedView.ts
//
// The STATELESS, blazing-fast grid model. Replaces the agent's client-tree
// SymbolTreeView (which materialized the whole catalog) with an indexer-paged
// model that holds ONLY a bounded rolling window — the browser never holds the
// whole tree, regardless of catalog size.
//
// It implements BOTH interfaces the grid consumes:
//   • the tree-view surface (getVisibleCount / getSymbolAtIndex / getRange /
//     toggleExpand / setFilter / setExpandAll / emit 'viewChanged') — so the
//     grid uses it directly as `activeView`, and
//   • SymbolGridDataSource (getSymbol / getValue / getLabel / getSymbolBufferRaw)
//     — so the grid renders each row's meta + value from the same window.
//
// Why this works without rewriting the grid: the grid only ever calls
// getSymbolAtIndex for the ~80 indices the virtualizer has on screen. As long as
// the window covers the viewport (± a prefetch margin), every on-screen index
// resolves locally. getVisibleCount() returns the indexer's totalMatching so the
// scrollbar reflects the full size; a scrollbar JUMP that lands outside the
// window triggers one offset-fetch (the proto `offset` field) and re-renders.
//
// The tree itself is linearized SERVER-SIDE by the indexer (it synthesizes the
// container nodes and applies expanded_paths); the window is just a slice of that
// linearization. Expand/collapse edits the expanded_paths set and re-queries —
// the row-flight animation (which needed a full client tree) degrades to a clean
// re-render, the one feature traded for statelessness-at-scale.

import { EventEmitter } from './emitter';
import { showResultError } from '../../errors';
import { t } from '@vitesse/base-ui';
import { getChartTheme } from './chartUtils';
import { ClockSync } from '../../../core/clockSync';
import { logicalType } from './rowValue';
import type { PagedBackend } from './pagedBackend';
import type { StreamFrame } from '../../../core/bridge/types';
import type { CatalogRow, PageQuery, TypedValue, SymbolPageData, SymbolOpResult, BulkProgress, BulkOutcome, BulkOp, ApplyToAllOutcome } from '../../../core/bridge/connect-client';
import type {
  SymbolGridDataSource,
  SymbolMeta,
  SymbolValue,
  ChartBufferRaw,
  ControllerChartConfig,
  IteratedSymbol,
  SymbolChanges,
  SymbolDataEvent,
  SymbolDataListener,
} from './dataSource';
import type { VisibleSymbol, ViewChangedEvent } from './types';

// 10 s chart window. The buffer-seed historian query asks for exactly this
// span, and the UI only ever shows ~10 s of sparkline — so a shorter window =
// proportionally fewer rows aggregated/shipped per window-shift (≈3× less than
// the old 30 s) at no visual cost. (RTT itself is dominated by the round-trip,
// not the row count, so the win is CPU/bytes, not latency.)
const CHART_CONFIG: ControllerChartConfig = { timeWindowMs: 10000, sampleRateHz: 5, theme: 'frost' };

// ── Fixed-rate resampled chart buffers ───────────────────────────────────────
// The window is a uniform time GRID (one slot per SLOT_MS). Each slot holds the
// value KNOWN at that grid time — from the historian backfill (the real last
// 10 s) and from live changes (forward of "now we're watching"); unknown slots
// hold NaN (the renderer skips them, so nothing is fabricated). Two properties:
//   • No left-edge jump — uniform slots fall off one fixed SLOT_MS step at a
//     time, and the grid extends PAST the render window by BUFFER_MARGIN_MS so
//     the slot actually being dropped is off-screen-left (x<0). The visible left
//     edge never pops.
//   • No fabricated history — a symbol just scrolled into view is NOT painted as
//     a 10 s flat line at its current value (we were never subscribed to it).
//     Its real last-10 s comes from the historian (seedFromHistory); the live
//     value + advance() grow the line forward from the moment we started
//     watching. Slots we genuinely don't know stay NaN → an honest gap, not a lie.
// The (timestamps, values, head, count, capacity) RING SHAPE is unchanged, so the
// renderer + hover tooltip (which binary-search the timestamps) work untouched.
const SLOT_MS = 33; // grid resolution: ~30 fps — matches the producer's 30 Hz fast rate
// (FastTickInterval) so EVERY slot gets its own real sample and the leading edge
// advances one sample per ~2 render frames (no 100 ms hold-then-jump). Sized to
// the refresh rate, not finer: a finer grid would hold empty slots no sample
// fills (and cost bandwidth to fill). SLOTS grows to ~348 (window+margin / 33ms);
// the per-frame min/max scan is cached on minMaxVersion, so render cost is flat.
// Grid spans the render window PLUS a margin, so the oldest-slot rotation happens
// off-screen-left (the renderer maps x against CHART_CONFIG.timeWindowMs). This is
// the "5% extra buffer so the deletion happens behind the scenes".
const BUFFER_MARGIN_MS = 1500; // ~15% beyond the 10 s render window
const BUFFER_WINDOW_MS = CHART_CONFIG.timeWindowMs + BUFFER_MARGIN_MS; // 11.5 s of grid
const SLOTS = Math.round(BUFFER_WINDOW_MS / SLOT_MS); // 115 slots (render window + off-screen margin)
const BUFFER_CAPACITY = SLOTS; // ring capacity == grid size
// Y-axis contract time-constant. The display range expands INSTANTLY to a new
// extreme (nothing is ever clipped) but eases DOWN toward the shrunk windowed
// range only over ~this long. This is deliberately MUCH longer than the 10 s
// window: a transient spike entering/leaving the window must NOT whip the scale,
// because that rescales the whole curve and reads as the data "flattening then
// un-flattening over time" (the bug). A long contract holds the signal's range
// envelope steady so a value of N always maps to the same height — the data is
// drawn CONSISTENTLY — and only a sustained change in the signal's range slowly
// re-ranges it. Expansion stays instant, so genuine new peaks still show at once.
const SCALE_TAU_MS = 9000;
interface SymbolBuffer {
  timestamps: Float64Array; // ring; evenly spaced, ascending in logical (head+i) order
  values: Float64Array; // ring; value known at each grid slot, or NaN if genuinely unknown
  count: number; // == SLOTS (the grid is always allocated; unknown slots are NaN)
  head: number; // logical-oldest physical index
  minMaxVersion: number; // bumped on slot rotation + value change (renderer's min/max cache key)
  cur: number; // last-known value (carry-forward source); NaN until a value is observed
  lastSlotTs: number; // timestamp of the NEWEST slot (logical head-1)
  hasLive: boolean; // a real live value has been observed (vs. only historian/empty)
  // The previous SAMPLE written via writeAt (stream order == ts order per symbol).
  // writeAt LOCF-stitches the span (lastSampleTs, ts) with lastSampleVal, so every
  // slot the stream covers is SAMPLE-derived — this is what erases the carry-
  // forward flood-fill the render loop writes while a multi-chunk seed is still
  // sweeping in (the "flatline fragments + spikes" bug). NaN until the first sample.
  lastSampleTs: number;
  lastSampleVal: number;
  // Per-slot marker, parallel to `values`: 1 = a REAL sample lives here (a
  // writeAt/writeCurrent point-write, or a known historian-seed slot); 0 =
  // carry-forward / NaN. The LOCF stitch only OVERWRITES 0-slots — so a guess
  // (carry-forward fill) can NEVER flatten ground truth that already drew
  // correctly. This is the fix for "the curve gets simplified/downsampled as it
  // scrolls left": once a slot holds a real sample it is immutable to the stitch.
  sampled: Uint8Array;
  // ── adaptive Y-axis scale (the "graph stops jumping vertically" fix) ──────────
  // The renderer used to recompute the windowed min/max EVERY frame, so the whole
  // curve remapped vertically the instant an extreme scrolled off the window edge
  // (the "graph moves/jumps/simplifies over time" bug). Instead we hold a STABLE
  // display range per symbol: it EXPANDS instantly to any new extreme (never clip)
  // and CONTRACTS on a slow ease (a departing extreme can't snap it). A steady
  // signal → the range converges → zero vertical movement; a transient settles
  // smoothly over ~SCALE_TAU_MS. NaN until the window first holds finite data.
  scaleMin: number;
  scaleMax: number;
  scaleWall: number; // backend-frame time of the last ease step (the dt source)
  // The windowed data min/max — the ease TARGET. Rescanned only when a sample or
  // rotation bumps minMaxVersion (targetVersion guard), so the per-frame cost is
  // just the ease, not an O(SLOTS) scan.
  targetMin: number;
  targetMax: number;
  targetVersion: number;
}

// Window geometry. A generous window means scrolling rarely crosses an edge, so
// the grid almost never re-fetches (→ no full re-render → no sparkline jitter)
// while RAM stays bounded to the window (~1200 rows + ≤1200 chart buffers ≈ 10 MB
// at 512×f64×2, vs ~150 MB for the whole catalog). fetch centers the window HALF
// before the focus; prefetch when the focus comes within EDGE of a boundary.
const WIN = 1200;
const HALF = 600;
const EDGE = 250;
// Fresh-load auto-expand (browse-on-demand TREE sources only — topic integrations
// et al.): on the very first visit (no saved nav state) walk the first-child chain
// open until the linearized view fills ~a screenful, so the user lands on real
// structure + a few real leaves instead of a lone collapsed root. One expand per
// applied page (each re-query lands the next level); bounded by a row target AND an
// op cap so a pathological deep/narrow tree can't cascade endlessly, and abandoned
// the instant the user interacts (toggle / filter / expand-all).
const AUTO_EXPAND_TARGET_ROWS = 40;
const AUTO_EXPAND_MAX_OPS = 12;
// VALUE-LEASE viewport scoping (PagedBackend.valueScopeViewport — integrations).
// The SCHEMA window stays WIN=1200 (cheap metadata, smooth scroll), but the live
// VALUE lease follows only the visible viewport ± this buffer, so the backend
// observes/polls/subscribes just what's on screen, not the whole window. The
// buffer pre-leases a screen-ish each way so small scrolls don't churn; a larger
// move re-scopes on scroll-settle (debounced). Off (controllers) → the lease is
// the whole window, the established path.
const VALUE_VIEWPORT_BUFFER = 80;
const VALUE_RESCOPE_DEBOUNCE_MS = 150; // re-scope after scrolling settles, not per frame
const VALUE_RESCOPE_MIN_SHIFT = 40; // ignore sub-buffer jitter — only re-scope on a real move

/**
 * Per-controller navigation state that OUTLIVES the IndexerPagedView (which is
 * re-created on every visit to a controller). It holds NO per-symbol data — just
 * the scrollbar size (total) + the tree shape (expand/filter/sort) + roughly
 * where the user was (focusIndex) — so it's a few hundred bytes per controller
 * regardless of catalog size. On return this makes the scrollbar correctly sized
 * BEFORE the first fetch (so the scroll-position restore lands on the right row)
 * and re-fetches the window the user left, not the top.
 */
interface PagedViewState {
  total: number;
  expandAll: boolean;
  expandedPaths: string[];
  collapsedPaths: string[];
  filter: string;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  focusIndex: number;
}
// Per-controller nav state (scroll/tree shape), kept across visits so returning to
// a controller restores its view. Capped LRU-by-save so an admin browsing hundreds
// of controllers over a long session doesn't grow it without bound (each entry is
// small, but the count is org-scale with no other eviction).
const VIEW_STATE_MAX = 64;
const viewStateCache = new Map<string, PagedViewState>();
// A sentinel expanded-path that matches no real parent — used to force the
// indexer's roots-only tree view (non-empty expanded_paths) when the user has
// collapsed to roots with nothing yet expanded. (Empty expanded_paths means
// "fully expanded flat", which is a different mode.)
const ROOTS_ONLY_SENTINEL = '\x00__roots_only__';

// One ClockSync for every paged view (see the field comment in IndexerPagedView).
const sharedClock = new ClockSync();

/** Logical-type mapping for a leaf's vendor data_type ("BOOL","REAL",…). */
function mapScalar(dt: string): string {
  const t = (dt || '').toLowerCase();
  if (t.includes('bool') || t === 'bit') return 'boolean';
  if (t.includes('real') || t.includes('float') || t.includes('double') || t === 'lreal') return 'number';
  if (
    t.includes('int') ||
    t === 'byte' ||
    t === 'word' ||
    t === 'dword' ||
    t === 'lword' ||
    t === 'sint' ||
    t === 'usint' ||
    t === 'uint' ||
    t === 'udint' ||
    t === 'ulint'
  )
    return 'integer';
  if (t.includes('string') || t.includes('char')) return 'string';
  return t || 'string';
}

/** Tree depth = number of '.' separators in the decoded path. */
function depthOf(path: string): number {
  let d = 0;
  for (let i = 0; i < path.length; i++) if (path.charCodeAt(i) === 0x2e) d++;
  return d;
}

export class IndexerPagedView extends EventEmitter implements SymbolGridDataSource {
  readonly controllerId: string;

  // Query state — the entire "tree state" the browser holds.
  // Two mutually-exclusive tree models:
  //   • FLAT (expandAll=true, the DEFAULT): every symbol visible; `collapsedPaths`
  //     is the blacklist of branches the user has collapsed (per-node collapse
  //     works via the indexer's collapsed_paths). "See it all, hide what I don't
  //     care about."
  //   • TREE (expandAll=false): roots first; `expandedPaths` is the whitelist of
  //     branches the user has expanded.
  private expandedPaths = new Set<string>();
  private collapsedPaths = new Set<string>();
  private expandAll = true;
  private filter = '';
  private sortColumn: string | null = null;
  private sortDirection: 'asc' | 'desc' = 'asc';

  // Rolling window — the ONLY tree data resident. rows[i] is absolute index offset+i.
  private offset = 0;
  private rows: CatalogRow[] = [];
  private total = 0;
  /** Parents whose children the last applied page CUT SHORT (window cap) — ""
   *  when nothing was truncated. See applyPage / getTruncatedParents. */
  private truncatedParents: string[] = [];
  private byPath = new Map<string, CatalogRow>();
  // Prefetched children per collapsed parent in the window (the indexer ships
  // the first ≤50 children of every collapsed parent in the viewport). Holding
  // them is what makes expand INSTANT — no network for the common case. Rebuilt
  // each window fetch, so it's window-bounded like everything else.
  private prefetched = new Map<string, CatalogRow[]>();
  // Subtrees removed by a FLAT-mode collapse, kept so re-expanding plays the same
  // row-flight animation in reverse (the indexer doesn't prefetch flat-mode
  // collapsed branches). Consumed on re-expand; pruned to the window each fetch.
  private collapseStash = new Map<string, CatalogRow[]>();
  // Structure stream — the dumb-viewport page source. ONE open at a time for the
  // current query; the backend pushes the snapshot + every recomputed page on
  // catalog change (the view never decides "did it change" — it renders what's
  // pushed). streamSeq supersedes frames from an older query after a nav; streamSig
  // dedups re-opens of the same query (so the per-frame getSymbolAtIndex path is a
  // cheap no-op when the window hasn't moved). The worker's supervise() reconnects
  // a dropped stream forever (re-snapshot), so there is no client-side cooldown or
  // empty-retry — a cold/building indexer just pushes rows when it has them.
  private structurePort?: MessagePort;
  private streamSeq = 0;
  private streamSig = '';

  // Values (viewport-scoped: the worker absorbs the firehose; only window symbols
  // get a resident value + chart buffer on the main thread).
  private values = new Map<string, SymbolValue>();
  // Backend change-time (ms) of each current value — the carry-forward anchor.
  // Lets a buffer flat-fill from the value's REAL last-change instant (LOCF), so
  // a symbol that hasn't changed in >window draws flat across the whole window
  // instead of a NaN gap — and correctly so even while the historian is behind.
  private valueTs = new Map<string, number>();
  private buffers = new Map<string, SymbolBuffer>();
  // Backend↔browser clock-offset estimator. The chart's time axis runs in the
  // BACKEND frame (clock.now()) so backend-stamped samples land in the right slot
  // even when the two clocks disagree (Docker VM drift after sleep, edge RTC skew).
  // MODULE-LEVEL singleton: the offset is a property of the deployment, not of
  // one view — sharing it means a re-created view starts with the converged
  // estimate instead of re-converging from 0 (which shifted every fresh
  // buffer's anchor by the full offset during the first seconds of each visit).
  private clock = sharedClock;
  // The view's ONE value stream — WINDOW-FOLLOW mode (see openWindowStream).
  // Opened at construction, closed at destroy; the api-gateway re-scopes it
  // server-side from this view's tagged page fetches. No re-subscribe, ever.
  private windowPort?: MessagePort;
  // The follow correlation id: every fetchSymbolPage this view issues carries
  // it (PageQuery.clientSession), and the value stream follows the same id.
  private readonly clientSession = crypto.randomUUID();
  private destroyed = false;
  // Visible viewport range (the grid's last getRange ask) — drives the value-lease
  // viewport hint when `be.valueScopeViewport`, so the backend produces only the
  // on-screen leaves. Updated on every render pass; re-scope is debounced.
  private visibleStart = 0;
  private visibleEnd = 0;
  private lastScopedCenter = -1; // viewport center the value-lease was last scoped to
  private valueScopeTimer?: ReturnType<typeof setTimeout>;

  private changeVersion = 0;
  private changedSymbols = new Set<string>();
  // Optimistic priority overrides: path → desired bit, held only until the
  // backend-confirmed CatalogRow.priority (republished descriptor → indexer →
  // SymbolPage) matches it, at which point the override is dropped and the row
  // is authoritative. This is what makes the red "!" both instant AND persistent.
  private priorityOverride = new Map<string, boolean>();
  // Optimistic lock overrides — same lifecycle as priorityOverride (held until the
  // backend-confirmed CatalogRow.locked catches up). Makes the padlock instant + persistent.
  private lockedOverride = new Map<string, boolean>();
  private _lastAccessed = Date.now();
  private restoredFocusIndex = 0; // where to load the window on activate() (0 = top, or where we left)
  // Fresh-load auto-expand state (see AUTO_EXPAND_*). `autoExpanding` is armed only
  // for a first-visit tree source; each applied page advances the first-child chain
  // one level until the screen fills, then disarms. `autoExpandOps` bounds it.
  private autoExpanding = false;
  private autoExpandOps = 0;

  constructor(
    /** The backend seam — a controller's symbols (SymbolIndex + plugin RPCs) or
     *  an integration's schema (IntegrationIndex). The engine is identical across
     *  both; only this adapter differs. */
    private readonly be: PagedBackend,
    /** Force TREE mode (roots-first, per-node expand-on-demand) and forbid the
     *  flat / expand-all mode. REQUIRED for browse-on-demand integration schemas:
     *  the backend pages a node's children only when that node is in
     *  `expandedPaths`, so flat mode (which sends NO expanded set, only a
     *  `collapsedPaths` blacklist) can never reveal a child, and "expand all"
     *  over a million/billion-node source is meaningless. It is also the correct
     *  UX for any topic tree (you browse into it, you don't flat-dump it). */
    private readonly treeMode = false,
  ) {
    super();
    this.controllerId = be.scopeId;
    if (treeMode) this.expandAll = false; // roots-first; expand pages children on demand
    // Restore the lightweight nav state from a prior visit (if any). Crucially,
    // `total` is set NOW so getVisibleCount() reports the real size before the
    // first fetch — the SymbolGrid scroll-restore (localStorage pixel offset)
    // then lands on the correct row instead of clamping to a size-0 scrollbar.
    const saved = viewStateCache.get(this.stateKey);
    if (saved) {
      this.total = saved.total;
      // In forced tree mode a saved flat/expand-all flag must NOT resurrect flat
      // mode (a browse-on-demand source can't be flattened) — pin expandAll=false.
      this.expandAll = treeMode ? false : saved.expandAll;
      this.expandedPaths = new Set(saved.expandedPaths);
      this.collapsedPaths = new Set(saved.collapsedPaths ?? []);
      this.filter = saved.filter;
      this.sortColumn = saved.sortColumn;
      this.sortDirection = saved.sortDirection;
      this.restoredFocusIndex = saved.focusIndex;
    }
    // Arm the fill-the-screen auto-expand whenever a browse-on-demand tree would
    // otherwise open on a lone collapsed root — i.e. nothing to restore (fresh visit
    // OR a saved-but-collapsed-to-root view) and no active filter. A saved view WITH
    // expansions restores the user's own shape (size > 0 → skip); controllers are flat
    // (treeMode false → skip). Any user interaction disarms it (toggle/filter/expand-all).
    if (treeMode && this.expandedPaths.size === 0 && !this.filter) this.autoExpanding = true;
    // Open the value stream NOW — in parallel with activate()'s first page
    // fetch. The server holds it until the first tagged page lands, then the
    // numeric snapshot + full-res chart seed flow immediately (no second
    // client round-trip after the page).
    this.openWindowStream();
  }

  /** viewStateCache key — gateway-scoped, so two gateways' same-id controllers
   *  (e.g. each gateway's "Core") never share nav state (scroll/tree/filter). */
  private get stateKey(): string {
    return this.be.storageKey;
  }

  /** Persist the nav state so the next visit restores scrollbar + tree shape. */
  private saveViewState(): void {
    viewStateCache.delete(this.stateKey); // re-insert at the end → most-recent (LRU recency)
    viewStateCache.set(this.stateKey, {
      total: this.total,
      expandAll: this.expandAll,
      expandedPaths: [...this.expandedPaths],
      collapsedPaths: [...this.collapsedPaths],
      filter: this.filter,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      focusIndex: this.offset + HALF, // window center ≈ where the user is looking
    });
    // Evict the least-recently-saved controller's state once over the cap.
    if (viewStateCache.size > VIEW_STATE_MAX) {
      const oldest = viewStateCache.keys().next().value;
      if (oldest !== undefined) viewStateCache.delete(oldest);
    }
  }

  // ── lifecycle (grid calls activate()/destroy() synchronously) ───────────────
  activate(): void {
    // Open the structure stream for the window the user LEFT (restoredFocusIndex,
    // 0 on a fresh visit) so a return-visit re-paints where they were. The stream
    // pushes the snapshot then every recomputed page as the catalog builds — so a
    // cold/lagging indexer just pushes rows when it has them (no empty-retry
    // watchdog), and its clientSession-tagged query scopes the value stream.
    void this.ensureStream(this.restoredFocusIndex, true);
  }
  /** Force-reopen the structure stream at the CURRENT window. Rarely needed now —
   *  the open stream already pushes membership changes (add/delete/discovery) live
   *  — but kept for explicit refresh callers. Reopens at the current position (or
   *  the top when empty, e.g. a filtered view whose only match was just deleted). */
  refetchWindow(): void {
    if (this.destroyed) return;
    this.ensureStream(this.total === 0 ? 0 : this.offset + HALF, true);
  }
  deactivate(): void {
    /* no background work to pause; window is request-driven */
  }
  destroy(): void {
    this.saveViewState(); // capture final scroll/tree shape for the next visit
    this.destroyed = true;
    this.closeStructureStream();
    if (this.valueScopeTimer) {
      clearTimeout(this.valueScopeTimer);
      this.valueScopeTimer = undefined;
    }
    this.windowPort?.postMessage({ kind: 'close' });
    this.windowPort?.close(); // close the main-thread handle too (not just the worker stream)
    this.windowPort = undefined;
    // Drop ALL per-symbol data so the view holds nothing after teardown — the
    // window, value, buffer, and override maps are released; only the few-hundred-
    // byte PagedViewState survives (in viewStateCache). Constant memory across
    // visits, regardless of how many controllers (or symbols) were browsed.
    this.rows = [];
    this.byPath.clear();
    this.collapseStash.clear();
    this.values.clear();
    this.valueTs.clear();
    this.buffers.clear();
    this.priorityOverride.clear();
    this.lockedOverride.clear();
    this.changedSymbols.clear();
    this.removeAllListeners();
  }
  touchAccess(): void {
    this._lastAccessed = Date.now();
  }
  get lastAccessed(): number {
    return this._lastAccessed;
  }
  getControllerId(): string {
    return this.controllerId;
  }
  /** Read-only source (integration) — the grid hides the per-row priority + lock
   *  write affordances (observe-while-watched; labels still apply). */
  get readOnlyActions(): boolean {
    return !!this.be.readOnlyActions;
  }
  // ── structure stream ─────────────────────────────────────────────────────────
  /** Identity of the window query at `offset` (ignoring the per-view clientSession,
   *  which is constant for a view). Two queries with the same sig are byte-identical;
   *  ensureStream uses it to skip reopening the stream for an unchanged query. */
  private querySig(offset: number): string {
    if (this.filter) return `f|${offset}|${this.filter}`;
    if (this.expandAll) return `a|${offset}|${[...this.collapsedPaths].sort().join(',')}`;
    return `e|${offset}|${[...this.expandedPaths].sort().join(',')}`;
  }

  private buildQuery(offset: number): PageQuery {
    const clientSession = this.clientSession; // window-follow: the fetch re-scopes the value stream
    const vp = this.viewportHint(); // value-lease scoping (integrations) — empty otherwise
    if (this.filter) return { offset, pageSize: WIN, fts: this.filter, clientSession, ...vp }; // flat search results
    // Flat mode: every symbol, minus the descendants of collapsed branches.
    if (this.expandAll) return { offset, pageSize: WIN, expandedPaths: [], collapsedPaths: [...this.collapsedPaths], clientSession, ...vp };
    const eps = this.expandedPaths.size ? [...this.expandedPaths] : [ROOTS_ONLY_SENTINEL];
    return { offset, pageSize: WIN, expandedPaths: eps, clientSession, ...vp };
  }

  /** The value-lease viewport hint: the path at the top of the visible viewport
   *  (minus a buffer) + the count of rows to lease (visible span + 2× buffer),
   *  clamped to the fetched window. Empty when the source doesn't viewport-scope
   *  values (controllers) or no rows are loaded yet — the api-gateway then leases
   *  the whole page (the established window-lease). */
  private viewportHint(): { viewportAnchorPath?: string; viewportSize?: number } {
    if (!this.be.valueScopeViewport || this.rows.length === 0) return {};
    const lo = this.offset;
    const hi = lo + this.rows.length - 1;
    const winStart = Math.max(lo, this.visibleStart - VALUE_VIEWPORT_BUFFER);
    const winEnd = Math.min(hi, this.visibleEnd + VALUE_VIEWPORT_BUFFER);
    if (winEnd < winStart) return {};
    const anchor = this.rows[winStart - lo];
    if (!anchor) return {};
    return { viewportAnchorPath: anchor.path, viewportSize: winEnd - winStart + 1 };
  }

  /** Re-scope the value lease to the current viewport after scrolling settles.
   *  Debounced + shift-gated so a steady viewport never re-leases and small jitter
   *  doesn't churn the backend's subscribe/poll set. Rides the existing
   *  window-follow path: a tagged twin carries the new viewport hint and the
   *  api-gateway swaps the lease to it (the page itself is discarded). */
  private scheduleValueRescope(): void {
    if (this.destroyed || this.valueScopeTimer) return;
    const center = (this.visibleStart + this.visibleEnd) >> 1;
    if (this.lastScopedCenter >= 0 && Math.abs(center - this.lastScopedCenter) < VALUE_RESCOPE_MIN_SHIFT) return;
    this.valueScopeTimer = setTimeout(() => {
      this.valueScopeTimer = undefined;
      if (this.destroyed || !this.be.valueScopeViewport || this.rows.length === 0) return;
      this.lastScopedCenter = (this.visibleStart + this.visibleEnd) >> 1;
      this.be.scopeValueStream(this.buildQuery(this.offset)); // tagged twin → re-scope only
    }, VALUE_RESCOPE_DEBOUNCE_MS);
  }

  /** Ensure the structure stream is open for the window covering `focusIndex`. The
   *  open stream pushes the snapshot page + every recomputed page on catalog change
   *  (server-side hash-compared), so the view renders what's pushed and never
   *  re-fetches per frame. A query change (scroll past the window edge / expand /
   *  collapse / filter / sort) opens a fresh stream for the new query and supersedes
   *  the old (seq guard); an UNCHANGED query is a cheap no-op (sig dedup), so the
   *  per-row getSymbolAtIndex hot path can call this freely. `force` reopens even an
   *  identical query (explicit refresh). The query carries clientSession, so each
   *  pushed page re-scopes the value plane server-side (followLeaves). */
  private ensureStream(focusIndex: number, force = false): void {
    if (this.destroyed) return;
    const offset = Math.max(0, focusIndex - HALF);
    const sig = this.querySig(offset);
    if (!force && this.structurePort && sig === this.streamSig) return; // already streaming this query
    this.streamSig = sig;
    const seq = ++this.streamSeq;
    this.closeStructureStream();
    // Optimistic instant first paint from a hover-prewarmed page (the default top
    // view) before the stream's snapshot lands; the snapshot supersedes it, and the
    // seq guard drops it if a newer query superseded this one meanwhile.
    if (offset === 0 && this.expandAll && !this.filter) {
      void this.be
        .takeFirstPage()
        .then((p) => {
          if (p && seq === this.streamSeq) this.applyPage(p, 0);
        })
        .catch(() => {});
    }
    void (async () => {
      try {
        const port = await this.be.openStructureStream(this.buildQuery(offset));
        if (!port) return; // source can't page (none today)
        if (this.destroyed || seq !== this.streamSeq) {
          port.postMessage({ kind: 'close' }); // superseded / torn down mid-await
          port.close();
          return;
        }
        port.onmessage = (e: MessageEvent<StreamFrame<SymbolPageData>>) => this.onStructureFrame(e.data, seq, offset);
        port.start();
        this.structurePort = port;
      } catch {
        /* the worker's supervise() reconnects the source forever; nothing to do */
      }
    })();
  }

  /** One structure frame: a COMPLETE recomputed page (snapshot-shaped — replace,
   *  not append). Dropped if a newer query superseded this stream; an error frame
   *  keeps the last page on screen (supervise reconnects + re-snapshots). */
  private onStructureFrame(frame: StreamFrame<SymbolPageData>, seq: number, offset: number): void {
    if (seq !== this.streamSeq) return; // superseded by a newer query's stream
    if (frame.kind !== 'snapshot') return; // 'error' → keep showing the last page
    const page = frame.items[0];
    if (page) this.applyPage(page, offset);
  }

  /** Close the current structure stream — tell the worker to tear the source down,
   *  then drop the main-thread port handle. */
  private closeStructureStream(): void {
    if (!this.structurePort) return;
    this.structurePort.postMessage({ kind: 'close' });
    this.structurePort.close();
    this.structurePort = undefined;
  }

  /** Apply ONE complete page to the resident window: swap rows, rebuild the
   *  prefetched-children index + byPath, prune every value-scoped map to the new
   *  window (the O(window) RAM invariant), reconcile chart buffers, persist nav
   *  state, and emit a full re-render. `offset` is the absolute index of rows[0]. */
  private applyPage(page: SymbolPageData, offset: number): void {
    this.offset = offset;
    this.rows = page.rows;
    this.total = page.totalMatching;
    // Parents this page could only show a PREFIX of (indexer window cap). Kept
    // for the grid's "showing the first N — narrow the search" chip: without it
    // a cut-short listing is indistinguishable from a complete one.
    this.truncatedParents = page.truncatedParents;
    // Rebuild the prefetched-children index for instant expand (reconstruct
    // CatalogRows from the page's parallel-array PrefetchGroups).
    this.prefetched = new Map();
    for (const g of page.prefetched) {
      const kids: CatalogRow[] = new Array(g.leafNames.length);
      for (let i = 0; i < g.leafNames.length; i++) {
        kids[i] = {
          path: `${g.parentPath}.${g.leafNames[i]}`,
          childCount: g.childCounts[i] ?? 0,
          dataType: g.dataTypes[i] ?? '',
          permission: g.permissions[i] ?? 'r',
          label: g.labels[i] ?? '',
          priority: false, // not carried in prefetch; the next full page fills it
          locked: false, // not carried in prefetch; the next full page fills it
          deletable: false, // not carried in prefetch; the next full page fills it
          detail: g.details[i] ?? '',
          kind: g.kinds[i] ?? '',
        };
      }
      this.prefetched.set(g.parentPath, kids);
    }
    this.byPath = new Map();
    for (const r of page.rows) {
      this.byPath.set(r.path, r);
      // Self-heal optimistic overrides: once the backend-confirmed bit matches
      // what we optimistically set, drop the override so the row is canonical.
      const ov = this.priorityOverride.get(r.path);
      if (ov !== undefined && ov === r.priority) this.priorityOverride.delete(r.path);
      const lov = this.lockedOverride.get(r.path);
      if (lov !== undefined && lov === r.locked) this.lockedOverride.delete(r.path);
    }
    // CONSTANT-MEMORY INVARIANT: every resident, value-scoped structure is pruned
    // to the current window each shift, so RAM is O(window) — NOT O(total). Scroll
    // through 10 M symbols at a flat ~window's worth of memory. `buffers` is pruned
    // in reconcileBuffers(); prune the rest here. (Off-window priority overrides are
    // safe to drop — the bit is persisted server-side, back on CatalogRow.priority.)
    for (const id of this.values.keys()) if (!this.byPath.has(id)) this.values.delete(id);
    for (const id of this.valueTs.keys()) if (!this.byPath.has(id)) this.valueTs.delete(id);
    for (const id of this.priorityOverride.keys()) if (!this.byPath.has(id)) this.priorityOverride.delete(id);
    for (const id of this.lockedOverride.keys()) if (!this.byPath.has(id)) this.lockedOverride.delete(id);
    for (const id of this.collapseStash.keys()) if (!this.byPath.has(id)) this.collapseStash.delete(id);
    this.reconcileBuffers();
    this.saveViewState(); // persist total + query state so a return-visit restores instantly
    this.changeVersion++;
    this.emitView({ type: 'full', visibleCount: this.total });
    // No value re-subscribe here: each pushed page's clientSession-tagged query
    // re-scopes the window-follow value stream server-side (the api-gateway derives
    // this page's leaf set + snapshots/seeds just the entering symbols). The value
    // stream itself was opened once, in the constructor.
    this.maybeAutoExpand(); // fresh-load: advance the fill-the-screen chain one level
  }

  /** Fresh-load auto-expand: while armed, expand the first-child chain one level
   *  per applied page until the linearized view fills ~a screenful, then disarm.
   *  "First row (document order) with children not yet expanded" IS the chain tip —
   *  an expanded node's children sit directly below it — so this walks straight
   *  down. Bounded by the row target + op cap; the constructor arms it only for a
   *  first-visit tree source, and any user interaction disarms it. */
  private maybeAutoExpand(): void {
    if (!this.autoExpanding) return;
    if (this.destroyed || this.filter || this.expandAll) return void (this.autoExpanding = false);
    if (this.total >= AUTO_EXPAND_TARGET_ROWS || this.autoExpandOps >= AUTO_EXPAND_MAX_OPS) return void (this.autoExpanding = false);
    const next = this.rows.find((r) => r.childCount > 0 && !this.expandedPaths.has(r.path));
    if (!next) return void (this.autoExpanding = false); // chain reached a leaf
    this.expandedPaths.add(next.path);
    this.autoExpandOps++;
    void this.ensureStream(0, true); // fresh-load only → load at the top
  }

  private emitView(ev: ViewChangedEvent): void {
    this.emit('viewChanged', ev);
  }

  private toVisible(r: CatalogRow): VisibleSymbol {
    return {
      symbolId: r.path,
      nodeId: r.path,
      level: depthOf(r.path),
      hasChildren: r.childCount > 0,
      isExpanded: this.isExpanded(r.path),
    };
  }

  // ── tree-view surface ───────────────────────────────────────────────────────
  getVisibleCount(): number {
    return this.total;
  }

  /** Parents whose listed children are a PREFIX of their true set (the indexer's
   *  window cap cut them short). Non-empty ⇒ the grid must show its "showing the
   *  first N — narrow the search" chip; the listing is NOT complete. */
  getTruncatedParents(): string[] {
    return this.truncatedParents;
  }

  getSymbolAtIndex(index: number): VisibleSymbol | undefined {
    const lo = this.offset;
    const hi = lo + this.rows.length;
    if (index >= lo && index < hi) {
      // Predictive: as the viewport nears a window edge, reopen the stream at the
      // shifted window early so contiguous scroll stays local + instant. The sig
      // dedup makes a same-window call a cheap no-op, so this is safe per row.
      if (index < lo + EDGE && lo > 0) this.ensureStream(lo);
      else if (index >= hi - EDGE && hi < this.total) this.ensureStream(hi);
      return this.toVisible(this.rows[index - lo]);
    }
    // Miss (scrollbar jump): reopen the stream around it — the sig dedup collapses
    // the ~80 visible indices that all miss at once to ONE stream, not 80. The row
    // paints on the next pushed page (viewChanged).
    this.ensureStream(index);
    return undefined;
  }

  /** The grid reports its on-screen index range every render (the virtualizer's
   *  visible items). For sources that viewport-scope values (integrations) this
   *  is the PRIMARY driver of the value-lease follow — the grid renders via
   *  getSymbolAtIndex, not getRange, so this is where the viewport is learned. */
  setVisibleRange(startIndex: number, endIndex: number): void {
    this.visibleStart = startIndex;
    this.visibleEnd = endIndex;
    if (this.be.valueScopeViewport) this.scheduleValueRescope();
  }

  getRange(startIndex: number, endIndex: number): VisibleSymbol[] {
    // Defensive: also learn the viewport here in case a path calls getRange.
    this.visibleStart = startIndex;
    this.visibleEnd = endIndex;
    if (this.be.valueScopeViewport) this.scheduleValueRescope();
    const out: VisibleSymbol[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const v = this.getSymbolAtIndex(i);
      if (v) out.push(v);
    }
    return out;
  }

  getIndexOfSymbol(symbolId: string): number {
    for (let i = 0; i < this.rows.length; i++) if (this.rows[i].path === symbolId) return this.offset + i;
    return -1;
  }

  buildIndexMap(): Map<string, number> {
    const m = new Map<string, number>();
    for (let i = 0; i < this.rows.length; i++) m.set(this.rows[i].path, this.offset + i);
    return m;
  }

  forEachVisibleSymbolId(cb: (symbolId: string) => boolean | void): void {
    for (const r of this.rows) if (cb(r.path) === false) break;
  }

  toggleExpand(nodeId: string): void {
    this.autoExpanding = false; // the user is driving the tree now — stop the fill-the-screen cascade
    if (this.filter) return; // FTS results are a flat match list — no tree ops
    if (this.expandAll) {
      // FLAT mode: per-node collapse via the blacklist. Both directions animate:
      // collapse stashes the subtree + plays the row-flight; expand restores that
      // stash + plays the SAME flight inversed. The ~5ms re-fetch then supersedes.
      if (this.collapsedPaths.has(nodeId)) {
        this.collapsedPaths.delete(nodeId);
        this.optimisticExpand(nodeId); // animate the reveal from the collapse stash
      } else {
        this.collapsedPaths.add(nodeId);
        this.optimisticCollapse(nodeId);
      }
    } else {
      // TREE mode: per-node expand via the whitelist.
      if (this.expandedPaths.has(nodeId)) {
        this.expandedPaths.delete(nodeId);
        this.optimisticCollapse(nodeId); // instant: hide the subtree now
      } else {
        this.expandedPaths.add(nodeId);
        this.optimisticExpand(nodeId); // instant: reveal prefetched children now
      }
    }
    // Authoritative re-open (the safety net): the expandedPaths change is a new
    // query, so reopen the stream — its snapshot supersedes the optimistic splice
    // with the server's exact linearization + total, and it then stays open to
    // push live updates for the expanded view. The optimistic edit makes the
    // interaction feel 0-RTT; the stream makes it correct + live.
    void this.ensureStream(this.offset + HALF, true);
  }

  /**
   * Splice a node's prefetched children into the window immediately (0-RTT
   * expand) and emit a 'splice' event so the grid plays its row-flight animation
   * (rows below slide down, children slide in). Lean on purpose — buffers + the
   * value re-scope happen in the re-fetch ~5 ms later, off the animation's hot
   * start. The re-fetch supersedes this with the authoritative window.
   */
  private optimisticExpand(nodeId: string): void {
    // Stash (flat-mode re-expand — the EXACT subtree that was collapsed) wins over
    // the indexer prefetch (tree-mode — direct children). Both give a 0-RTT reveal,
    // so the expand plays the same row-flight animation the collapse did, inversed.
    const kids = this.collapseStash.get(nodeId) ?? this.prefetched.get(nodeId);
    if (!kids || kids.length === 0) return; // nothing cached → the fetch reveals it (no animation)
    const li = this.rows.findIndex((r) => r.path === nodeId);
    if (li < 0) return;
    this.collapseStash.delete(nodeId); // consumed
    this.rows = [...this.rows.slice(0, li + 1), ...kids, ...this.rows.slice(li + 1)];
    for (const k of kids) this.byPath.set(k.path, k);
    this.total += kids.length; // optimistic; the fetch corrects for the >50 tail
    this.changeVersion++;
    this.emitView({ type: 'splice', visibleCount: this.total, spliceIndex: this.offset + li + 1, insertCount: kids.length, removeCount: 0 });
  }

  /**
   * Remove a node's descendant rows from the window immediately (0-RTT collapse)
   * and emit a 'splice' so the grid animates (children slide up behind the
   * parent, rows below slide up to fill).
   */
  private optimisticCollapse(nodeId: string): void {
    const li = this.rows.findIndex((r) => r.path === nodeId);
    if (li < 0) return;
    const prefix = `${nodeId}.`;
    let end = li + 1;
    while (end < this.rows.length && this.rows[end].path.startsWith(prefix)) end++;
    const removed = end - (li + 1);
    if (removed === 0) return;
    this.collapseStash.set(nodeId, this.rows.slice(li + 1, end)); // keep the subtree for an animated re-expand
    for (let i = li + 1; i < end; i++) this.byPath.delete(this.rows[i].path);
    this.rows = [...this.rows.slice(0, li + 1), ...this.rows.slice(end)];
    this.total -= removed; // optimistic; the fetch corrects for off-window descendants
    this.changeVersion++;
    this.emitView({ type: 'splice', visibleCount: this.total, spliceIndex: this.offset + li + 1, removeCount: removed, insertCount: 0 });
  }

  setExpandAll(expanded: boolean): void {
    this.autoExpanding = false; // explicit bulk expand/collapse overrides the fill-the-screen cascade
    // A forced-tree view (browse-on-demand integration schema) can't be flattened:
    // flat mode sends no expanded set, so the backend would page nothing. Ignore.
    if (this.treeMode) return;
    this.expandAll = expanded;
    // "Expand all" / "collapse all" reset BOTH tree models to their clean state.
    this.expandedPaths.clear();
    this.collapsedPaths.clear();
    this.collapseStash.clear();
    void this.ensureStream(0, true);
  }

  isExpanded(nodeId: string): boolean {
    // Flat: expanded unless explicitly collapsed. Tree: expanded only if whitelisted.
    if (this.expandAll) return !this.collapsedPaths.has(nodeId);
    return this.expandedPaths.has(nodeId);
  }

  get expandAllMode(): boolean {
    return this.expandAll;
  }

  setFilter(query: string): void {
    const q = query.trim();
    if (q === this.filter) return;
    this.filter = q;
    void this.ensureStream(0, true);
  }
  getFilter(): string {
    return this.filter;
  }

  getSort(): { column: string | null; direction: 'asc' | 'desc' } {
    return { column: this.sortColumn, direction: this.sortDirection };
  }
  setSort(column: string | null): void {
    if (column === this.sortColumn) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    // The indexer sorts by path natively; column sort is a later wire addition.
    // Re-fetch so the view reflects the (path-ordered) window regardless.
    void this.ensureStream(this.offset + HALF, true);
  }

  getLevel(symbolId: string): number {
    return depthOf(symbolId);
  }
  getNodeId(symbolId: string): string {
    return symbolId;
  }
  getNodeByNodeId(): undefined {
    return undefined; // TreeNode objects don't exist in the paged model
  }

  // ── SymbolGridDataSource (per-row data, served from the window) ──────────────
  getRootSymbols(): Set<string> {
    return new Set();
  }
  getChildren(): Set<string> {
    return new Set();
  }
  getSubtreeSize(): number {
    return 0;
  }
  getDepth(symbolId: string): number {
    return depthOf(symbolId);
  }
  getParent(symbolId: string): string | undefined {
    const dot = symbolId.lastIndexOf('.');
    return dot < 0 ? undefined : symbolId.slice(0, dot);
  }
  isStreamingSymbols(): boolean {
    return false;
  }

  getSymbol(symbolId: string): SymbolMeta | undefined {
    const r = this.byPath.get(symbolId);
    if (!r) return undefined;
    // Having children and having no value are different questions — a broker
    // topic row carries its raw message AND has its flattened fields under it.
    // See rowValue.ts; the row's `kind` is what tells the two apart.
    const type = logicalType(r.dataType, r.childCount, r.kind, mapScalar);
    return { type, permission: r.permission || 'r' };
  }
  getValue(symbolId: string): SymbolValue {
    return this.values.get(symbolId);
  }
  /** When this symbol's value last CHANGED, from the producer's own stamp. Already
   *  maintained for the chart's LOCF seeding and pruned with the window, so the
   *  freshness column costs no extra state. Undefined = nothing has arrived yet
   *  (which the grid renders as absent, not as zero seconds ago). */
  getValueTs(symbolId: string): number | undefined {
    return this.valueTs.get(symbolId);
  }
  getLabel(symbolId: string): string | undefined {
    return this.byPath.get(symbolId)?.label || undefined;
  }
  // `symbolId` here is the DECODED path (byPath is path-keyed); putLabel encodes
  // it once. Empty/undefined deletes. Optimistic like setPriority: paint now,
  // persist to user_data KV, and the indexer's overlay re-stream self-heals.
  setLabel(symbolId: string, label: string | undefined): void {
    const text = label ?? '';
    const row = this.byPath.get(symbolId);
    if (row) row.label = text;
    this.changedSymbols.add(symbolId);
    this.changeVersion++;
    void this.be.putLabel(symbolId, text).catch(() => {});
  }
  getPriority(symbolId: string): boolean {
    // Optimistic override wins until the backend-confirmed row catches up.
    const ov = this.priorityOverride.get(symbolId);
    if (ov !== undefined) return ov;
    return this.byPath.get(symbolId)?.priority ?? false;
  }
  setPriority(symbolId: string, on: boolean): void {
    this.priorityOverride.set(symbolId, on);
    this.changedSymbols.add(symbolId);
    this.changeVersion++;
    // Full round-trip: the plugin persists the bit + republishes the descriptor,
    // which flows indexer → SymbolPage.priority → getPriority (override self-heals
    // on the next window fetch). The button reflects the optimistic state instantly.
    void this.be.setPriority(symbolId, on).catch(() => {});
  }
  getLocked(symbolId: string): boolean {
    const ov = this.lockedOverride.get(symbolId);
    if (ov !== undefined) return ov;
    return this.byPath.get(symbolId)?.locked ?? false;
  }
  setLocked(symbolId: string, locked: boolean): void {
    const previous = this.lockedOverride.get(symbolId);
    this.lockedOverride.set(symbolId, locked);
    this.changedSymbols.add(symbolId);
    this.changeVersion++;
    // Optimistic like priority, but with one difference that matters: a padlock
    // is a SECURITY control, so an optimism that survives a refusal shows
    // protection the plugin declined to apply. The plugin (the sole lock
    // authority) may refuse a symbol a lock protects nothing on — read-only, or
    // a container rather than a write target — and it refuses per-symbol while
    // the RPC as a whole succeeds. So revert on refusal and say why.
    void this.be
      .setLocked(symbolId, locked)
      .then((res) => {
        if (!res || res.ok) return;
        if (previous === undefined) this.lockedOverride.delete(symbolId);
        else this.lockedOverride.set(symbolId, previous);
        this.changedSymbols.add(symbolId);
        this.changeVersion++;
        showResultError(new Error(res.error || res.errorCode), t(locked ? 'grid.lockRefused' : 'grid.unlockRefused'));
      })
      .catch((err) => {
        if (previous === undefined) this.lockedOverride.delete(symbolId);
        else this.lockedOverride.set(symbolId, previous);
        this.changedSymbols.add(symbolId);
        this.changeVersion++;
        showResultError(err, t(locked ? 'grid.lockRefused' : 'grid.unlockRefused'));
      });
  }
  /** User-deletable leaf? (plugin-decided, carried on the row.) Drives the trash. */
  getDeletable(symbolId: string): boolean {
    return this.byPath.get(symbolId)?.deletable ?? false;
  }
  /** Delete one symbol via the plugin's deleteSymbols RPC. The plugin removes it
   *  (and won't re-discover it) → the indexer drops it → the row vanishes on the
   *  next window fetch. Resolves the per-symbol outcome. */
  async deleteSymbol(symbolId: string): Promise<{ ok: boolean; error: string }> {
    return this.be.deleteSymbol(symbolId);
  }
  /** Write one symbol via the plugin's writeSymbols RPC; resolve its per-symbol
   *  result (ok + structured error_code/error) so the editor can surface LOCKED
   *  etc. `dataType` (the raw wire type) drives the value→protobuf oneof. */
  async writeValue(symbolId: string, value: boolean | string, dataType: string): Promise<SymbolOpResult> {
    return this.be.writeValue(symbolId, value, dataType);
  }
  /** Bulk lock/unlock the given paths. The chunked executor runs in the worker;
   *  `onProgress` is Comlink-proxied so the worker can tick it across the boundary. */
  bulkSetLocked(paths: string[], locked: boolean, onProgress: (p: BulkProgress) => void): Promise<BulkOutcome> {
    return this.be.bulkSetLocked(paths, locked, onProgress);
  }
  bulkSetPriority(paths: string[], priority: boolean, onProgress: (p: BulkProgress) => void): Promise<BulkOutcome> {
    return this.be.bulkSetPriority(paths, priority, onProgress);
  }
  /** Full-catalog path list (select-all bulk). Pages the indexer server-side —
   *  the grid's resident window can't enumerate the whole selection itself. */
  getAllSymbolPaths(): Promise<string[]> {
    // Pass the active text filter so a filtered select-all enumerates ONLY the
    // matching leaves (correctness: 'filter Motors -> select-all -> lock' must
    // lock Motors, not the whole controller) - and stays a small, fast set.
    return this.be.fetchAllPaths(this.filter);
  }
  /** Whole-controller bulk op — ONE RPC over every symbol. The grid routes here
   *  only when the selection is the WHOLE controller (select-all, no deselections,
   *  no filter), so no enumeration/chunking is needed. */
  applyToAll(op: BulkOp): Promise<ApplyToAllOutcome> {
    return this.be.applyToAll(op);
  }
  getSymbolBufferRaw(symbolId: string): ChartBufferRaw | null {
    const buf = this.buffers.get(symbolId);
    if (!buf) return null; // grid is always SLOTS-sized; all-NaN renders as "no data"
    // Roll the grid forward to NOW on read (called per visible row per redraw →
    // O(visible), not O(window)): old slots scroll off the left, fresh
    // carry-forward slots enter on the right, so the window stays full as time
    // passes even when no value is changing. Off-screen buffers don't advance
    // until scrolled into view, where the first read catches them up (≤SLOTS).
    const nowMs = this.clock.now();
    this.advance(buf, nowMs); // backend frame — consistent with the samples on the grid
    this.adaptScale(buf, nowMs); // smooth, expand-fast/contract-slow Y range
    return {
      timestamps: buf.timestamps,
      values: buf.values,
      count: buf.count,
      head: buf.head,
      capacity: BUFFER_CAPACITY,
      minMaxVersion: buf.minMaxVersion,
      nowMs, // continuous backend-frame anchor for the renderer's smooth scroll
      scaleMin: buf.scaleMin,
      scaleMax: buf.scaleMax,
    };
  }

  /**
   * Advance the STABLE Y-axis display range one ease step (see SymbolBuffer.scaleMin).
   * The renderer used to derive min/max from the visible window EVERY frame, so the
   * whole curve jumped vertically the instant an extreme crossed the left window
   * edge. Here the range:
   *   • EXPANDS INSTANTLY to any new windowed extreme — a spike is never clipped;
   *   • CONTRACTS by a time-based exponential ease (SCALE_TAU_MS) toward the
   *     shrunk windowed range — so a departing extreme can't snap the scale.
   * A steady signal → target == scale → no movement at all. The windowed TARGET is
   * rescanned only when minMaxVersion changes (a sample/rotation); the ease itself
   * runs every read so an off-screen buffer scrolled back into view (large dt →
   * factor→1) snaps straight to the current target instead of easing from a stale one.
   */
  private adaptScale(buf: SymbolBuffer, now: number): void {
    if (buf.targetVersion !== buf.minMaxVersion) {
      buf.targetVersion = buf.minMaxVersion;
      const startTime = now - CHART_CONFIG.timeWindowMs;
      let tmin = Infinity;
      let tmax = -Infinity;
      for (let i = 0; i < SLOTS; i++) {
        const v = buf.values[i];
        if (buf.timestamps[i] >= startTime && Number.isFinite(v)) {
          if (v < tmin) tmin = v;
          if (v > tmax) tmax = v;
        }
      }
      buf.targetMin = tmin;
      buf.targetMax = tmax;
    }
    const tmin = buf.targetMin;
    const tmax = buf.targetMax;
    if (!Number.isFinite(tmin) || !Number.isFinite(tmax)) {
      // No finite data in the window → drop the scale so the renderer falls back
      // to its own scan / no-data handling (never freeze a stale range).
      buf.scaleMin = NaN;
      buf.scaleMax = NaN;
      buf.scaleWall = now;
      return;
    }
    if (!Number.isFinite(buf.scaleMin) || !Number.isFinite(buf.scaleMax)) {
      buf.scaleMin = tmin; // seed: first finite window → adopt exactly, no ease
      buf.scaleMax = tmax;
      buf.scaleWall = now;
      return;
    }
    const dt = now - buf.scaleWall;
    buf.scaleWall = now;
    if (dt <= 0) return; // same-frame re-read (hover hit-test) → keep the scale
    const f = 1 - Math.exp(-dt / SCALE_TAU_MS); // dt→∞ ⇒ f→1 ⇒ snap (re-entered buffer)
    buf.scaleMin = tmin < buf.scaleMin ? tmin : buf.scaleMin + (tmin - buf.scaleMin) * f;
    buf.scaleMax = tmax > buf.scaleMax ? tmax : buf.scaleMax + (tmax - buf.scaleMax) * f;
  }
  getControllerChartConfig(): ControllerChartConfig {
    // Theme is the user's live, persisted choice (the chart-settings picker);
    // window/rate are fixed. The grid's render loop reads this each frame.
    return { ...CHART_CONFIG, theme: getChartTheme() };
  }
  *iterateSymbols(): Iterable<IteratedSymbol> {
    // Window-scoped — the search command engine over the resident window only.
    for (const r of this.byPath.values()) {
      yield { symbol_id: r.path, type: r.childCount > 0 ? 'object' : mapScalar(r.dataType), permission: r.permission };
    }
  }
  getChanges(): SymbolChanges {
    return { version: this.changeVersion, changedSymbols: this.changedSymbols };
  }
  clearConsumedChanges(): void {
    this.changedSymbols = new Set();
  }

  on(event: SymbolDataEvent | 'viewChanged', listener: SymbolDataListener): this {
    return super.on(event, listener);
  }
  off(event: SymbolDataEvent | 'viewChanged', listener: SymbolDataListener): this {
    return super.off(event, listener);
  }

  // ── live values ─────────────────────────────────────────────────────────────
  /**
   * Open the view's ONE value stream — WINDOW-FOLLOW mode. Called once, from
   * the constructor, in parallel with the first page fetch.
   *
   * The api-gateway scopes the stream server-side to the leaf rows of this
   * view's tagged page fetches (PageQuery.clientSession == followSession) and
   * re-scopes it on every window shift — so a scroll/expand/filter costs ZERO
   * extra round-trips for values, and only the ENTERING symbols are
   * snapshotted + seeded (not the whole ~1200-leaf window, as the old
   * close-and-reopen per shift did).
   *
   * Per (re)scope the stream carries, in order:
   *   1. the numeric snapshot of entering leaves (current value, incl. static
   *      ones that haven't changed in the chart window),
   *   2. the full-resolution value_changes seed of the last BUFFER_WINDOW_MS,
   *   3. the live tail — every sample, no compaction loss.
   * One handler (onWindowValues) routes all three identically, so ordering
   * races between phases are structurally impossible.
   */
  private openWindowStream(): void {
    void (async () => {
      try {
        const port = await this.be.openWindowStream(this.clientSession, BUFFER_WINDOW_MS);
        if (!port) return; // source has no live value plane yet (e.g. integrations — 5b)
        if (this.destroyed) {
          port.postMessage({ kind: 'close' }); // view torn down mid-await
          port.close();
          return;
        }
        port.onmessage = (e: MessageEvent<StreamFrame<TypedValue>>) => this.onWindowValues(e.data);
        port.start();
        this.windowPort = port;
      } catch {
        /* values are best-effort; the tree still renders */
      }
    })();
  }

  /**
   * The unified value handler — numeric column AND sparkline from one stream.
   * Every event is routed the same way regardless of phase (snapshot / seed /
   * live), which is what makes the stream's phases seamless:
   *   • NUMERIC: newest-ts-wins. A re-scope's backfill can deliver samples
   *     older than the displayed value (the seed re-covers the window);
   *     applying only ts >= current keeps the column monotone.
   *   • CHART: the FIRST sample for a buffer seeds the flat LOCF baseline
   *     (the value provably held from its last change to now — covers static
   *     and sparse leaves); every subsequent sample is writeAt — placed by its
   *     own backend ts, span-stitched (see writeAt) so the seed sweep erases
   *     the baseline where real samples cover it.
   * Off-window symbols are skipped cheaply (byPath gate).
   */
  private onWindowValues(frame: StreamFrame<TypedValue>): void {
    if (frame.kind === 'error') return;
    if (frame.kind !== 'snapshot') return;
    for (const v of frame.items) if (v.tsMs > 0) this.clock.observe(v.tsMs);
    // A first-touch buffer gets its WHOLE seed burst (this one frame carries it)
    // LOCF-resampled onto EVERY slot in one pass. Seeding from only the FIRST
    // sample + writeAt-ing the rest left the between-sample slots flat-poisoned —
    // seedFromHistory marks carry-forward slots sampled=1, which blocks writeAt's
    // stitch — so the sparse seed region deleted in big chunks on the left for the
    // first ~window (smooth only once it scrolled off). Accumulate per symbol.
    const seedBatch = new Map<string, { ts: number; num: number }[]>();
    for (const v of frame.items) {
      if (!this.byPath.has(v.symbolId)) continue;
      const prevTs = this.valueTs.get(v.symbolId) ?? 0;
      if (v.tsMs <= 0 || v.tsMs >= prevTs) {
        this.values.set(v.symbolId, v.value);
        if (v.tsMs > 0) this.valueTs.set(v.symbolId, v.tsMs);
      }
      if (v.num === null) continue;
      const buf = this.buffers.get(v.symbolId);
      if (!buf) continue;
      const ts = v.tsMs > 0 ? v.tsMs : this.clock.now();
      if (!buf.hasLive) {
        (seedBatch.get(v.symbolId) ?? seedBatch.set(v.symbolId, []).get(v.symbolId)!).push({ ts, num: v.num });
      } else {
        this.writeAt(buf, ts, v.num);
      }
    }
    for (const [id, hist] of seedBatch) {
      const buf = this.buffers.get(id);
      if (!buf) continue;
      hist.sort((a, b) => a.ts - b.ts);
      buf.cur = hist[hist.length - 1].num;
      buf.hasLive = true;
      this.seedFromHistory(buf, this.clock.now(), hist); // dense LOCF over EVERY 33ms slot
    }
  }

  private chartable(type: string): boolean {
    return type === 'number' || type === 'integer' || type === 'boolean';
  }

  /**
   * Keep a chart buffer for EVERY chartable leaf in the window — so all of them
   * always show a sparkline — and drop buffers for symbols that scrolled out, so
   * RAM stays bounded to the window. A new buffer is a NaN grid (nothing
   * fabricated); the real last-N s arrives from the historian (seedBuffersFromHistory)
   * and live values grow it forward.
   */
  private reconcileBuffers(): void {
    const now = this.clock.now();
    for (const id of this.buffers.keys()) {
      if (!this.byPath.has(id)) this.buffers.delete(id);
    }
    for (const r of this.rows) {
      if (r.childCount > 0 || this.buffers.has(r.path)) continue;
      if (!this.chartable(mapScalar(r.dataType))) continue;
      const buf = this.makeBuffer(now);
      const num = this.liveNum(r.path);
      const ts = this.valueTs.get(r.path);
      if (num !== null && ts !== undefined) {
        // LOCF: the value held constant from its last-change instant (ts) until
        // now, so seed the buffer with that single (ts, value) sample.
        // seedFromHistory carry-forwards it — flat from ts→now, and when ts
        // predates the window, flat across the WHOLE window (the symbol has
        // provably not changed). Correct even if the historian never answers,
        // because it rides on the authoritative live value, not on history.
        buf.cur = num;
        buf.hasLive = true;
        this.seedFromHistory(buf, now, [{ ts, num }]);
      } else if (num !== null) {
        // Value but no backend ts (shouldn't happen for real plugins) — right
        // edge only, no claim about the past.
        this.writeCurrent(buf, now, num);
      }
      this.buffers.set(r.path, buf);
    }
  }

  /** Current live value of `path` as a chartable number (bool→0/1), or null. */
  private liveNum(path: string): number | null {
    const v = this.values.get(path);
    return typeof v === 'boolean' ? (v ? 1 : 0) : typeof v === 'number' ? v : null;
  }
  /** Allocate a NaN grid anchored at `now`. Every slot is "unknown" until the
   *  historian backfill (seedFromHistory) or live values (writeCurrent) fill it —
   *  nothing is fabricated. count is SLOTS (the grid is always allocated); the
   *  renderer skips NaN slots, so an all-NaN buffer draws as "no data", not a lie. */
  private makeBuffer(now: number): SymbolBuffer {
    const ts = new Float64Array(SLOTS);
    const values = new Float64Array(SLOTS);
    const t0 = now - (SLOTS - 1) * SLOT_MS;
    for (let i = 0; i < SLOTS; i++) {
      ts[i] = t0 + i * SLOT_MS;
      values[i] = NaN;
    }
    return {
      timestamps: ts,
      values,
      sampled: new Uint8Array(SLOTS), // all 0 → every slot starts as "not a real sample"
      count: SLOTS,
      head: 0,
      minMaxVersion: 0,
      cur: NaN,
      lastSlotTs: now,
      hasLive: false,
      lastSampleTs: NaN,
      lastSampleVal: NaN,
      scaleMin: NaN,
      scaleMax: NaN,
      scaleWall: now,
      targetMin: NaN,
      targetMax: NaN,
      targetVersion: -1,
    };
  }

  /**
   * Roll the grid forward to `now`. Each elapsed slot rotates in on the right
   * carrying `cur` — which is the real last-known value once we've seen one (the
   * line genuinely held), or NaN before then (an honest gap, NOT a fabricated
   * flat). Idempotent + cheap. Bumps minMaxVersion on rotation so the renderer
   * recomputes its windowed min/max as extremes scroll off.
   */
  private advance(buf: SymbolBuffer, now: number): void {
    let elapsed = Math.floor((now - buf.lastSlotTs) / SLOT_MS);
    if (elapsed <= 0) return;
    if (elapsed > SLOTS) elapsed = SLOTS;
    for (let i = 0; i < elapsed; i++) {
      buf.lastSlotTs += SLOT_MS;
      buf.timestamps[buf.head] = buf.lastSlotTs; // oldest physical slot becomes the newest
      buf.values[buf.head] = buf.cur; // NaN until a value is known → no fabrication
      buf.sampled[buf.head] = 0; // carry-forward, not a real sample → the stitch may fill it
      buf.head = (buf.head + 1) % SLOTS;
    }
    buf.minMaxVersion++;
  }

  /** A live value change at `now`: roll the grid up, then stamp the newest slot.
   *  This grows the line FORWARD from when we started watching — real, because we
   *  are subscribed now and the value is held. It does NOT backfill the past. */
  private writeCurrent(buf: SymbolBuffer, now: number, v: number): void {
    this.advance(buf, now);
    buf.cur = v;
    buf.hasLive = true;
    buf.values[(buf.head - 1 + SLOTS) % SLOTS] = v; // newest slot reflects the change immediately
    buf.sampled[(buf.head - 1 + SLOTS) % SLOTS] = 1; // a real sample → immutable to the stitch
    buf.minMaxVersion++;
  }

  /**
   * Place a live sample at its OWN backend timestamp `ts` — NOT at the instant the
   * frame arrived. This is the load-bearing fix for "the sine doesn't look like a
   * sine": the relay + the worker's coalescing flush make samples arrive in jittery
   * clumps, so anchoring each one to clock.now()-at-arrival shears a clean 30 Hz
   * sine into noise. Anchoring to the sample's true ts (already corrected into the
   * backend frame by ClockSync) drops every sample into the slot it belongs to, so
   * a burst delivered together each lands in its own slot — no collapse onto one
   * slot, no lost detail, no jitter.
   *
   * The right edge ("now") scrolls independently and smoothly in getSymbolBufferRaw
   * via advance(clock.now()) on every render frame. Here we ONLY write the value:
   *   • ts at/after the newest slot → fresh sample: roll forward (carry cur into any
   *     real gap via LOCF) and stamp the new newest slot.
   *   • ts within the window but behind the right edge (the normal case — a sample's
   *     true time trails "now" by its in-flight delay) → drop it straight into the
   *     slot its timestamp indexes, leaving the right-edge scroll untouched.
   *   • older than the window → off-screen, ignored.
   * Samples arrive in ascending ts (NATS preserves order), so cur ends at the
   * newest value for the right-edge carry-forward.
   */
  private writeAt(buf: SymbolBuffer, ts: number, v: number): void {
    // ── transport-robustness guards (backlog re-seed / out-of-order / clock skew) ──
    // The window-follow stream can deliver ANCIENT samples (a re-auth re-seed
    // replaying a long backlog — observed 90 min old, latMs in the millions) and
    // OUT-OF-ORDER ones (seed+live interleave, worker coalescing). The incremental
    // LOCF below assumes every sample is the in-order newest. A stale sample slipped
    // in would (a) move the lastSample anchor backwards, so (b) the NEXT real
    // sample's stitch flood-fills the whole grid with a wrong value — that's the
    // "spike draws straight to the next plateau, wiping the 1s between" corruption.
    // Two guards make the buffer bulletproof regardless of what the transport sends:
    //   1) DROP anything older than the whole grid — off-screen-left; only the
    //      anchor could be poisoned by it, never anything visible.
    const oldestSlotTs = buf.lastSlotTs - (SLOTS - 1) * SLOT_MS;
    if (ts < oldestSlotTs) return;
    buf.hasLive = true;
    //   2) An OUT-OF-ORDER point (older than the last applied sample) is PLACED in
    //      its slot but must NOT advance the edge, move cur, stitch, or move the
    //      anchor — those all assume it's the newest. So it can fill its own slot
    //      but can never rewrite the LOCF spans around real data.
    if (Number.isFinite(buf.lastSampleTs) && ts < buf.lastSampleTs) {
      const back = Math.round((buf.lastSlotTs - ts) / SLOT_MS);
      if (back >= 0 && back < SLOTS) {
        const idx = (buf.head - 1 - back + 2 * SLOTS) % SLOTS;
        buf.values[idx] = v;
        buf.sampled[idx] = 1;
        buf.minMaxVersion++;
      }
      return;
    }
    if (ts >= buf.lastSlotTs) {
      this.advance(buf, ts); // fills any real gap with the PREVIOUS cur (LOCF)…
      buf.cur = v; // …then this sample becomes the held value…
      const newest = (buf.head - 1 + SLOTS) % SLOTS;
      buf.values[newest] = v; // …and stamps the newest slot.
      buf.sampled[newest] = 1; // a real sample → immutable to the stitch
    } else {
      const slotsBack = Math.round((buf.lastSlotTs - ts) / SLOT_MS);
      if (slotsBack < SLOTS) {
        const idx = (buf.head - 1 - slotsBack + 2 * SLOTS) % SLOTS;
        buf.values[idx] = v; // its true slot, a few back from the scrolling edge
        buf.sampled[idx] = 1; // a real sample → immutable to the stitch
        // FORWARD-FILL the latency window (ts → right edge) with v — THE slow-signal
        // fix. The value became v at ts, so every slot from ts to now is v. But this
        // sample arrived 200-1000 ms LATE (delivery latency), and advance() already
        // painted that whole band with the PRE-CHANGE value while it was in flight.
        // The stitch below only repairs OLDER-than-ts; left alone, the stale band
        // sits NEWER-than-ts and scrolls left forever as the wrong value — e.g. a
        // plateau that dropped to a low still renders at the plateau height across
        // the latency window ("the 1s show as a fat 3 line"). Tiny + invisible on
        // fast/low-latency signals (the next sample overwrites it instantly), large
        // and very visible on slow ones — exactly the fast-fine / slow-broken split.
        // Carry-forward only; a newer out-of-order real sample (sampled=1) is kept.
        for (let k = slotsBack - 1; k >= 0; k--) {
          const slot = (buf.head - 1 - k + 2 * SLOTS) % SLOTS;
          if (buf.sampled[slot]) continue;
          buf.values[slot] = v;
        }
      }
      // LOCF-STITCH the span back to the PREVIOUS sample: rewrite every slot in
      // (lastSampleTs, ts) with lastSampleVal — the value the line truly held
      // between the two samples. This makes the whole stream-covered region
      // SAMPLE-derived, structurally erasing the junk the render loop's
      // advance(clock.now()) flood-fills while a multi-chunk seed is still
      // sweeping in (without this, any slot a point write skips — Math.round
      // collisions of the 30 Hz producer on the 33 ms grid — keeps that junk:
      // the flatline-fragments-and-spikes band). Bounded by the window (k<SLOTS),
      // so a baseline-an-hour-ago previous sample just LOCF-fills the visible
      // left — which is exactly correct ("it provably held that value").
      if (Number.isFinite(buf.lastSampleTs) && buf.lastSampleTs < ts) {
        const prevBack = Math.round((buf.lastSlotTs - buf.lastSampleTs) / SLOT_MS);
        const stop = Math.min(prevBack, SLOTS); // exclusive: don't restamp prev's own slot
        for (let k = slotsBack + 1; k < stop; k++) {
          const slot = (buf.head - 1 - k + 2 * SLOTS) % SLOTS;
          if (buf.sampled[slot]) continue; // NEVER flatten a real sample with a carry-forward guess
          buf.values[slot] = buf.lastSampleVal; // gap between two samples → LOCF (only carry-forward/NaN slots)
        }
      }
      buf.cur = v; // latest known value still feeds the right-edge carry-forward
    }
    buf.lastSampleTs = ts;
    buf.lastSampleVal = v;
    buf.minMaxVersion++;
  }

  /**
   * Resample the historian backfill (sorted ascending (ts,num)) into the grid via
   * carry-forward — this is the REAL last-N s, the only source of pre-subscription
   * history. Slots BEFORE the first historian sample stay NaN (we genuinely don't
   * know the value then — honest gap, not a fabricated flat). The live current
   * value, if one already arrived, wins the right edge (history's tail lags by the
   * historian's ingest latency).
   */
  private seedFromHistory(buf: SymbolBuffer, now: number, hist: { ts: number; num: number }[]): void {
    const liveCur = buf.hasLive ? buf.cur : null; // capture before the resample
    const t0 = now - (SLOTS - 1) * SLOT_MS;
    let si = 0;
    let lastVal = NaN; // unknown until the first sample — NO pre-window fabrication
    while (si < hist.length && hist[si].ts <= t0) {
      lastVal = hist[si].num;
      si++;
    }
    buf.head = 0;
    buf.count = SLOTS;
    buf.lastSlotTs = now;
    for (let i = 0; i < SLOTS; i++) {
      const t = t0 + i * SLOT_MS;
      while (si < hist.length && hist[si].ts <= t) {
        lastVal = hist[si].num;
        si++;
      }
      buf.timestamps[i] = t;
      buf.values[i] = lastVal; // NaN before the first real sample; carry-forward after
      buf.sampled[i] = Number.isNaN(lastVal) ? 0 : 1; // known history is real (immutable to the stitch); pre-history NaN is not
    }
    if (liveCur !== null) {
      buf.values[SLOTS - 1] = liveCur; // live snapshot wins the right edge over history's tail
      buf.sampled[SLOTS - 1] = 1; // the live value is real → immutable to the stitch
      buf.cur = liveCur;
    } else if (!Number.isNaN(lastVal)) {
      buf.cur = lastVal;
    }
    // Anchor the writeAt stitch on the newest seeded point, so the first
    // subsequent stream sample LOCF-stitches from real data, not from NaN.
    if (hist.length > 0) {
      buf.lastSampleTs = hist[hist.length - 1].ts;
      buf.lastSampleVal = hist[hist.length - 1].num;
    }
    buf.minMaxVersion++;
  }
}
